/**
 * Company: SPHMedia
 * Description: LifestyleLuxe Section Layout
 */

import { ReactElement, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Await } from "react-router-dom";
import { ArticleDataObject } from "@app/types/Cue";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { LifestyleLuxeContext } from "@app/types/Page";
import BtLuxeLogo from "@assets/logo-btluxe.svg";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import LuxeCard from "@components/ArticleCard/LuxeCard";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import CustomError from "@components/Error/CustomError";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
// Components import
import Header from "@components/Header/Header";
import { useCategoriesFromPath } from "@components/Header/helpers";
import LoadMore from "@components/LoadMore/LoadMore";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import LifestyleLuxeMasthead from "@pages/Section/components/Lifestyle/LifestyleLuxeMasthead";
import LifestyleNews from "@pages/Section/components/Lifestyle/LifestyleNews";
import { sectionNavigationItems } from "@util/constant";
import { useContext } from "@util/getContext";
import { GoogleAdsSlotFactory } from "@util/helpers";
import axios from "axios";

const articleSet = new Set<string>();

export default function LifestyleLuxeLayout(): ReactElement {
  const context: LifestyleLuxeContext = useContext();
  const { parentCategory, childCategory } = useCategoriesFromPath();
  const { isPrestitialEnabled, topOverlayImpressions } = useGlobalAdSetting({
    targeting: { key: "page", value: "listing" },
  });

  const [post, setPost] = useState<ArticleDataObject[]>([]);
  const lifestyleLuxeData = context.data.lifestyleLuxe;
  const initialPosts = lifestyleLuxeData?.slice(1, 15);

  const firstArticle = lifestyleLuxeData?.slice(0, 1).at(0);
  const firstArticleMainSection = firstArticle?.sections?.at(0);

  if (post.length === 0 && !post.at(-1)?.sort) {
    lifestyleLuxeData?.forEach((art: ArticleDataObject) => {
      articleSet.add(art.id);
    });
    setPost(initialPosts || []);
  }

  const loadMore = async () => {
    const epoch = post.at(-1)?.sort?.[0] ?? 0;
    const score = post.at(-1)?.sort?.[1] ?? 0;
    const sort = [new Date(epoch).toISOString(), score.toString() ?? ""];

    const response = await axios.post("/_plat/api/v1/more-stories", {
      category: "lifestyle_bt-luxe",
      parentCategory,
      sort: sort,
      size: 12,
    });

    const openSearchResponse = response.data;

    if (openSearchResponse.length > 0) {
      const updatedPost = [...post];

      openSearchResponse.forEach((art: ArticleDataObject) => {
        if (!articleSet.has(art.id)) {
          updatedPost.push(art);
          articleSet.add(art.id);
        }
      });
      setPost(updatedPost);
    }
  };

  return (
    <Await resolve={context} errorElement={<CustomError />}>
      {({ kind, error }) => {
        return kind !== "section" || error ? (
          <h1>Error</h1>
        ) : (
          <div className="w-full">
            <Helmet>
              <title>{context.data.title}</title>
            </Helmet>

            <Advertisement
              rootClassName="bg-stripes py-[12px]"
              adUnitProps={{
                type: AdvertisementTypeEnum.LB1,
                slot: GoogleAdsSlotFactory.lb1("lifestyle/bt-luxe"),
              }}
            />

            <PrestitialAdWrapper
              slot={GoogleAdsSlotFactory.prestitial("lifestyle/bt-luxe")}
              isPrestitialEnabled={isPrestitialEnabled}
              topOverlayImpressions={topOverlayImpressions}
            />

            <Header
              mastheadTitle={parentCategory}
              mastheadCustomElement={
                <LifestyleLuxeMasthead
                  mastheadPath={parentCategory}
                  logoSrc={BtLuxeLogo}
                  logoWidth="150"
                  logoHeight="44"
                />
              }
            />

            {parentCategory && sectionNavigationItems?.[parentCategory] ? (
              <div className="flex-none bg-gray-250">
                <Breadcrumb
                  rootClassName="m-auto container"
                  parentCategory={parentCategory}
                  childCategory={childCategory}
                  categoryDataMapping={sectionNavigationItems}
                />
              </div>
            ) : null}

            {firstArticle ? (
              <LuxeCard
                id={firstArticle.id}
                title={
                  firstArticle.displaySetting?.displayHeadline ||
                  firstArticle.title
                }
                media={firstArticle.media}
                kicker={firstArticle.kicker?.fields?.[0]?.value}
                section={firstArticle.sections?.at(0)}
                paywall={firstArticle.paywall?.contentAccess === "1"}
                blurb={firstArticle.blurb}
                rootClassName="mb-16"
                defaultImage={
                  firstArticleMainSection
                    ? { directoryName: firstArticleMainSection.uniqueName }
                    : undefined
                }
                slug={firstArticle.slug}
              />
            ) : null}

            <Container rootClassName="pb-8">
              <LifestyleNews
                data={post}
                parentCategory={parentCategory}
                childCategory={childCategory}
              />

              <LoadMore loadText="More Stories" onLoadMore={loadMore} />
            </Container>

            <FooterSubscribeRow />
          </div>
        );
      }}
    </Await>
  );
}
